import { useEffect, useRef, useCallback } from 'react'
import { useAuth } from './useSupabaseAuth.jsx'

/**
 * Custom hook untuk auto-logout setelah periode inaktivitas
 * @param {number} timeoutMinutes - Waktu timeout dalam menit (default: 60 menit)
 */
const useAutoLogout = (timeoutMinutes = 60) => {
  const { user, signOut } = useAuth()
  const timeoutRef = useRef(null)
  const warningTimeoutRef = useRef(null)
  const lastActivityRef = useRef(Date.now())
  
  // Konversi menit ke milidetik
  const timeoutMs = timeoutMinutes * 60 * 1000

  // Fungsi untuk reset timer
  const resetTimer = useCallback(() => {
    if (!user) return
    
    // Clear timer yang ada
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current)
    }
    
    // Update waktu aktivitas terakhir
    lastActivityRef.current = Date.now()
    
    // Set timer baru dengan warning 5 menit sebelum logout
    const warningTime = timeoutMs - (5 * 60 * 1000) // 5 menit sebelum logout
    
    // Timer untuk warning
    if (warningTime > 0) {
      warningTimeoutRef.current = setTimeout(() => {
        if (user && confirm('Anda akan logout otomatis dalam 5 menit karena tidak aktif. Klik OK untuk tetap login atau Cancel untuk logout sekarang.')) {
          // User memilih untuk tetap login, reset timer
          resetTimer()
        } else {
          // User memilih logout atau tidak merespons
          signOut()
        }
      }, warningTime)
    }
    
    // Timer untuk auto-logout
    timeoutRef.current = setTimeout(async () => {
      console.log('Auto-logout: User tidak aktif selama', timeoutMinutes, 'menit')
      try {
        await signOut()
        // Tampilkan notifikasi kepada user
        alert('Anda telah logout otomatis karena tidak aktif selama 1 jam.')
      } catch (error) {
        console.error('Error during auto-logout:', error)
      }
    }, timeoutMs)
  }, [user, signOut, timeoutMs, timeoutMinutes])

  // Fungsi untuk menangani aktivitas user
  const handleUserActivity = useCallback(() => {
    if (user) {
      resetTimer()
    }
  }, [user, resetTimer])

  // Setup event listeners untuk mendeteksi aktivitas user
  useEffect(() => {
    if (!user) {
      // Clear timer jika user tidak login
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current)
        warningTimeoutRef.current = null
      }
      return
    }

    // Daftar event yang dianggap sebagai aktivitas user
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown'
    ]

    // Tambahkan event listeners
    events.forEach(event => {
      document.addEventListener(event, handleUserActivity, true)
    })

    // Set timer awal
    resetTimer()

    // Cleanup function
    return () => {
      // Remove event listeners
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity, true)
      })
      
      // Clear timers
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current)
      }
    }
  }, [user, handleUserActivity, resetTimer])

  // Cleanup saat component unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  // Return informasi tentang status auto-logout
  return {
    isActive: !!user,
    lastActivity: lastActivityRef.current,
    resetTimer,
    timeoutMinutes
  }
}

export default useAutoLogout